import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  UserCircle, SignOut, Phone, CalendarCheck, MapPin, Wallet,
  Gift, Clock, CaretRight
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

export default function MRProfilePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ attendance: 0, visits: 0, expenses: 0, gifts: 0 });

  useEffect(() => {
    const month = new Date().toISOString().slice(0, 7);
    Promise.all([
      api.getAttendance(month),
      api.getVisits(),
      api.getExpenses(month),
      api.getGifts(),
    ]).then(([att, vis, exp, gif]) => {
      setStats({
        attendance: att.data.filter(a => a.status === 'present').length,
        visits: vis.data.length,
        expenses: exp.data.reduce((s, e) => s + e.amount, 0),
        gifts: gif.data.filter(g => g.delivered).length,
      });
    });
  }, []);

  const menuItems = [
    { label: 'Attendance History', icon: CalendarCheck, color: '#007AFF', path: '/mr', desc: `${stats.attendance} days this month` },
    { label: 'Visit Log', icon: MapPin, color: '#34C759', path: '/mr/visits', desc: `${stats.visits} total visits` },
    { label: 'Expenses', icon: Wallet, color: '#FFCC00', path: '/mr/expenses', desc: `₹${stats.expenses.toLocaleString()} this month` },
    { label: 'Gifts Delivered', icon: Gift, color: '#FF3B30', path: '/mr/gifts', desc: `${stats.gifts} delivered` },
    { label: 'Khata Book', icon: Clock, color: '#0A2540', path: '/mr/khata', desc: 'View ledger' },
  ];

  return (
    <div className="space-y-4">
      {/* Profile Header */}
      <div className="px-4 pt-5 pb-4 text-center" style={{ background: 'linear-gradient(180deg, #0A2540 0%, #0A2540 70%, #F9F9FA 100%)' }}>
        <div className="w-20 h-20 rounded-2xl mx-auto flex items-center justify-center text-3xl font-extrabold text-white"
          style={{ background: '#007AFF', border: '3px solid rgba(255,255,255,0.2)' }}>
          {user?.name?.charAt(0) || 'M'}
        </div>
        <h2 className="text-xl font-extrabold mt-4 text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {user?.name}
        </h2>
        <p className="text-sm flex items-center justify-center gap-1 mt-1" style={{ color: 'rgba(255,255,255,0.5)' }}>
          <Phone size={14} /> {user?.phone}
        </p>
        <span className="inline-block mt-3 px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
          style={{ background: 'rgba(0,122,255,0.15)', color: '#007AFF' }}>
          Medical Representative
        </span>
      </div>

      {/* Stats Grid */}
      <div className="px-4 grid grid-cols-2 gap-3">
        <div className="p-3 rounded-xl border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
          <p className="text-[9px] font-bold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Attendance</p>
          <p className="text-xl font-extrabold mt-1" style={{ color: '#007AFF', fontVariantNumeric: 'tabular-nums' }}>{stats.attendance} days</p>
        </div>
        <div className="p-3 rounded-xl border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
          <p className="text-[9px] font-bold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Visits</p>
          <p className="text-xl font-extrabold mt-1" style={{ color: '#34C759', fontVariantNumeric: 'tabular-nums' }}>{stats.visits}</p>
        </div>
      </div>

      {/* Menu Items */}
      <div className="px-4 space-y-1.5">
        {menuItems.map((item, i) => {
          const Icon = item.icon;
          return (
            <button key={i} onClick={() => navigate(item.path)}
              className="w-full p-3.5 rounded-xl border flex items-center gap-3 text-left active:scale-[0.98] transition-transform"
              style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: `${item.color}10` }}>
                <Icon size={20} weight="duotone" style={{ color: item.color }} />
              </div>
              <div className="flex-1">
                <p className="text-sm font-bold" style={{ color: '#0A2540' }}>{item.label}</p>
                <p className="text-[10px]" style={{ color: '#8A94A6' }}>{item.desc}</p>
              </div>
              <CaretRight size={16} style={{ color: '#E2E4E9' }} />
            </button>
          );
        })}
      </div>

      {/* Logout */}
      <div className="px-4 pb-4">
        <Button onClick={logout}
          className="w-full h-12 text-sm font-bold rounded-xl"
          variant="outline"
          style={{ color: '#FF3B30', borderColor: '#FF3B3030' }}>
          <SignOut size={18} className="mr-2" /> Sign Out
        </Button>
      </div>
    </div>
  );
}
